import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Container,
  Chip,
  CircularProgress,
  Alert,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Home as HomeIcon,
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Lightbulb as LightbulbIcon,
  Business as BusinessIcon,
  AccountCircle as AccountCircleIcon,
  Lock as LockIcon,
  ReceiptLong as BillingIcon,
  BugReport as SiteAuditIcon,
  FindInPage as PageAuditIcon,
  History as AuditHistoryIcon,
  AutoAwesome as RecommendationsIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../store/auth.store';
import { useSiteStore } from '../store/site.store';
import { useGA4Store } from '../store/ga4.store';
import { SiteSelector } from './SiteSelector';
import { metricsService } from '../services/metricsService';
import { ga4ContextService } from '../services/ga4ContextService';
import { organizationService } from '../services/organizationService';
import { siteIntegrationService } from '../services/siteIntegrationService';
import config from '../config';
import { useQuery } from '@tanstack/react-query';
import { ChangePasswordModal } from './ChangePasswordModal';
import { getAnalyticsQuickLinks, getDefaultAnalyticsPath, isCustomerProfile } from '../utils/analyticsNavigation';

const drawerWidth = 240;

const AppLayout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user, getEffectiveTenantId } = useAuthStore();
  const { setGA4Status } = useGA4Store();
  const { setSites, setLoading, setError, selectedSite, selectedPageId, setSelectedPage } = useSiteStore();
  const effectiveTenantId = getEffectiveTenantId();

  // Load sites with TanStack Query (30 min cache)
  const { data: sites, isLoading: sitesLoading, error: sitesError } = useQuery({
    queryKey: ['sites', user?.tenantId],
    queryFn: metricsService.getSites,
    enabled: !!user?.tenantId,
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000,    // 1 hour
  });

  // Update site store when sites load
  useEffect(() => {
    console.log('🔍 AppLayout - Sites loading state:', {
      sitesLoading,
      sitesCount: sites?.length || 0,
      selectedSiteId: selectedSite?.id,
      userTenantId: user?.tenantId,
      userTenantName: user?.tenantName,
      sitesError: sitesError?.message
    });
    
    if (sites) {
      console.log('✅ Sites loaded successfully:', sites);
      setSites(sites);
      setLoading(false);
    }
    if (sitesLoading) {
      setLoading(true);
    }
    if (sitesError) {
      console.error('❌ Sites loading error:', sitesError);
      setError(sitesError instanceof Error ? sitesError.message : 'Failed to load sites');
    }
  }, [sites, sitesLoading, sitesError, setSites, setLoading, setError, selectedSite?.id, user?.tenantId, user?.tenantName]);

  // Fetch GA4 connection status on mount
  // Use effective tenant (proxy if switched, else home)

  // Agency branding comes directly from auth state - populated at login, no extra call needed
  const agencyLogoUrl = user?.agencyLogoUrl;
  const agencyName = user?.agencyName;
  const hasAgencyBranding = !!(user?.agencyCode && agencyLogoUrl);
  const drawerLogoSrc = hasAgencyBranding ? agencyLogoUrl : '/asneris.png';
  const drawerBrandName = hasAgencyBranding ? (agencyName || user?.agencyCode || 'Agency') : 'SEO Engine';
  const isAnalyticsRoute = location.pathname === '/analytics' || location.pathname.startsWith('/analytics/');
  const shouldBlockOutlet = sitesLoading && !isAnalyticsRoute;
  const shouldShowSitesError = sitesError && !isAnalyticsRoute;

  useQuery({
    queryKey: ['ga4-status', effectiveTenantId],
    queryFn: async () => {
      if (!effectiveTenantId) return null;
      const tenantId = Number(effectiveTenantId);
      if (isNaN(tenantId)) {
        console.warn('Invalid tenantId:', effectiveTenantId);
        return null;
      }
      try {
        const status = await ga4ContextService.getGA4Status(tenantId);
        setGA4Status(status);
        return status;
      } catch (error) {
        console.log('GA4 not connected or error fetching status:', error);
        return null;
      }
    },
    enabled: !!effectiveTenantId && !isNaN(Number(effectiveTenantId)),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const { data: organization } = useQuery({
    queryKey: ['layout-organization', effectiveTenantId],
    queryFn: () => organizationService.getOrganization(String(effectiveTenantId)),
    enabled: Boolean(effectiveTenantId),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const { data: selectedSiteIntegrations } = useQuery({
    queryKey: ['layout-site-integrations', selectedSite?.id],
    queryFn: () => siteIntegrationService.getIntegrationStatus(Number(selectedSite?.id)),
    enabled: Boolean(selectedSite?.id),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // Listen for site changes and redirect to dashboard if on page-specific route
  useEffect(() => {
    const handleSiteChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      console.log('🔄 Site changed:', customEvent.detail);
      
      // If on a page-specific route, redirect to dashboard
      const pageSpecificRoutes = ['/pages/', '/oci/', '/momentum/', '/validation/', '/analytics/'];
      if (pageSpecificRoutes.some(route => location.pathname.includes(route))) {
        console.log('📍 Redirecting to dashboard after site switch');
        navigate('/');
      }
    };

    window.addEventListener('site-changed', handleSiteChange);
    return () => {
      window.removeEventListener('site-changed', handleSiteChange);
    };
  }, [location.pathname, navigate]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleChangePassword = () => {
    setAnchorEl(null);
    setChangePasswordOpen(true);
  };

  const handleBilling = () => {
    setAnchorEl(null);
    navigate('/billing');
  };

  const handleUserMenuLogout = () => {
    setAnchorEl(null);
    handleLogout();
  };

  // Get mode directly from the selected site (no API call needed)
  const siteMode = selectedSite?.mode;
  const isEarlyStage = siteMode?.trim().replace(/-/g, '_').toUpperCase() === 'EARLY_STAGE';
  const normalizedPlanTier = (organization?.planTier || 'STARTER').trim().toUpperCase();
  const isStarterPlan = normalizedPlanTier === 'STARTER' || normalizedPlanTier === 'FREE';
  const selectedSiteHasPropertyReference = Boolean(
    selectedSite?.gscPropertyId ||
    selectedSite?.bingId ||
    selectedSite?.ga4PropertyId ||
    selectedSite?.pinterestId
  );
  const selectedSiteHasProperties = (selectedSiteIntegrations?.configuredCount || 0) > 0 || selectedSiteHasPropertyReference;
  const canOpenSearchEngineReports = !isStarterPlan && selectedSiteHasProperties;

  // Debug logging
  React.useEffect(() => {
    if (selectedSite) {
      console.log('🔍 Selected Site:', {
        id: selectedSite.id,
        name: selectedSite.name,
        mode: selectedSite.mode,
        hasMode: !!selectedSite.mode,
        isEarlyStage
      });
    }
  }, [selectedSite, isEarlyStage]);

  // Search Engine Progress menu items
  const searchEngineProgressItems = [
    { text: 'Organization', icon: <SettingsIcon />, path: '/organization' },
    { text: 'Billing', icon: <BillingIcon />, path: '/billing' },
    { text: 'Site Configuration', icon: <SettingsIcon />, path: '/webtools/config', disabled: isStarterPlan },
    // { text: 'Sitemap Status', icon: <SettingsIcon />, path: '/customer/sitemap' },
  ];

  // Technical SEO Health menu items
  const technicalSeoHealthItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/early-stage' },
    { text: 'Action Center', icon: <RecommendationsIcon />, path: '/actions-next' },
    { text: 'Page-Level Readiness', icon: <CheckCircleIcon />, path: '/early-stage/pages'},
  ];

  const isCustomerUser = isCustomerProfile(user);
  const analyticsQuickLinks = getAnalyticsQuickLinks(user, selectedSite?.id);
  const showAnalyticsSection = !isCustomerUser && (analyticsQuickLinks.length > 0 || Boolean(selectedSite?.id) || !user?.agencyCode);
  const homePath = getDefaultAnalyticsPath(user);
  const isHomeRoute = location.pathname === '/' || location.pathname === homePath || location.pathname.startsWith('/analytics/');

  // Opportunities menu item — single entry; tabs inside the page handle sub-navigation
  const opportunitiesItems = [
    { text: 'Search Performance', icon: <TrendingUpIcon />, path: '/customer/performance', disabled: !canOpenSearchEngineReports },
    { text: 'Opportunities', icon: <LightbulbIcon />, path: '/opportunities', disabled: !canOpenSearchEngineReports },
  ];

  // AI Audit items - Check if AI menu is enabled via environment configuration
  const isAIMenuEnabled = config.VITE_AI_ENABLED_MENU !== 'false';
  
  const aiAuditItems = [
    { text: 'Start Site Audit', icon: <SiteAuditIcon />, path: '/customer/audit/site' },
    { text: 'Submit Page Audit', icon: <PageAuditIcon />, path: '/customer/audit/page' },
    { text: 'Audit History', icon: <AuditHistoryIcon />, path: '/customer/audit/tracking' },
    { text: 'Recommendations', icon: <RecommendationsIcon />, path: '/customer/audit/recommendations' },
  ];

  const drawer = (
    <Box>
      <Toolbar>
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <img
              src={drawerLogoSrc}
              alt={drawerBrandName}
              style={{ height: 26, width: 'auto' }}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = '/asneris.png';
              }}
            />
            <Typography variant="subtitle1" noWrap sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
              {drawerBrandName}
            </Typography>
          </Box>
          {siteMode && (
            <Chip 
              label={siteMode.replace('_', ' ')} 
              size="small" 
              color={isEarlyStage ? 'warning' : 'success'}
              sx={{ alignSelf: 'flex-start' }}
            />
          )}
        </Box>
      </Toolbar>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton
            selected={isHomeRoute}
            onClick={() => {
              navigate(homePath);
              setMobileOpen(false);
            }}
          >
            <ListItemIcon><HomeIcon /></ListItemIcon>
            <ListItemText primary="Home" />
          </ListItemButton>
        </ListItem>
      </List>
      <Divider />
      <List>
        <ListItem>
          <ListItemText 
            primary="Site Technical SEO Health" 
            primaryTypographyProps={{ 
              variant: 'caption', 
              color: 'text.secondary',
              sx: { textTransform: 'uppercase', fontWeight: 600 }
            }} 
          />
        </ListItem>
        {technicalSeoHealthItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => {
                navigate(item.path);
                setMobileOpen(false);
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      {showAnalyticsSection ? (
        <>
          <List>
            <ListItem>
              <ListItemText
                primary="Analytics"
                primaryTypographyProps={{
                  variant: 'caption',
                  color: 'text.secondary',
                  sx: { textTransform: 'uppercase', fontWeight: 600 },
                }}
              />
            </ListItem>
            {analyticsQuickLinks.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => {
                    navigate(item.path);
                    setMobileOpen(false);
                  }}
                >
                  <ListItemIcon>
                    {item.icon === 'person' ? <AccountCircleIcon /> : <BusinessIcon />}
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          <Divider />
        </>
      ) : null}
      <List>
        <ListItem>
          <ListItemText 
            primary="Search Engine Reports" 
            primaryTypographyProps={{ 
              variant: 'caption', 
              color: 'text.secondary',
              sx: { textTransform: 'uppercase', fontWeight: 600 }
            }} 
          />
        </ListItem>
        {opportunitiesItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              disabled={item.disabled}
              selected={location.pathname === item.path || location.pathname.startsWith(item.path + '/')}
              onClick={() => {
                if (item.disabled) return;
                navigate(item.path);
                setMobileOpen(false);
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        <ListItem>
          <ListItemText 
            primary="Organization & Configuration" 
            primaryTypographyProps={{ 
              variant: 'caption', 
              color: 'text.secondary',
              sx: { textTransform: 'uppercase', fontWeight: 600 }
            }} 
          />
        </ListItem>
        {searchEngineProgressItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              disabled={item.disabled}
              selected={
                location.pathname === item.path ||
                (item.path === '/organization' && location.pathname.startsWith('/organization/'))
              }
              onClick={() => {
                if (item.disabled) return;
                navigate(item.path);
                setMobileOpen(false);
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      {isAIMenuEnabled && (
        <>
          <Divider />
          <List>
            <ListItem>
              <ListItemText
                primary="AI Audit"
                primaryTypographyProps={{
                  variant: 'caption',
                  color: 'text.secondary',
                  sx: { textTransform: 'uppercase', fontWeight: 600 },
                }}
              />
            </ListItem>
            {aiAuditItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => {
                    navigate(item.path);
                    setMobileOpen(false);
                  }}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', width: '100%' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          {/* Mobile menu button */}
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          {/* Site Selector with Cache Invalidation */}
          <Box sx={{ mr: 3 }}>
            <SiteSelector />
          </Box>

          {/* Tenant name — only for regular customers (agency users see it in the session banner) */}
          {!user?.agencyCode && user?.tenantName && (
            <Chip
              icon={<BusinessIcon fontSize="small" />}
              label={user.tenantName}
              size="small"
              variant="outlined"
              sx={{ color: 'inherit', borderColor: 'rgba(255,255,255,0.5)', fontWeight: 600, mr: 1 }}
            />
          )}

          {/* Display selected page if any */}
          {selectedPageId && (
            <Chip 
              label={`Page ID: ${selectedPageId}`} 
              size="small" 
              onDelete={() => setSelectedPage(null)}
              sx={{ mr: 2, color: 'inherit', borderColor: 'inherit' }}
              variant="outlined"
            />
          )}

          <Box sx={{ flexGrow: 1 }} />

          {/* User account menu — top right */}
          <IconButton color="inherit" onClick={handleUserMenuOpen} size="large">
            <AccountCircleIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleUserMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {user?.email && (
              <MenuItem disabled>
                <ListItemIcon><AccountCircleIcon fontSize="small" /></ListItemIcon>
                {user.email}
              </MenuItem>
            )}
            {user?.authProvider !== 'google' && (
              <MenuItem onClick={handleChangePassword}>
                <ListItemIcon><LockIcon fontSize="small" /></ListItemIcon>
                Change Password
              </MenuItem>
            )}
            <MenuItem onClick={handleBilling}>
              <ListItemIcon><BillingIcon fontSize="small" /></ListItemIcon>
              Billing
            </MenuItem>
            <MenuItem onClick={handleUserMenuLogout}>
              <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          backgroundColor: (theme) => theme.palette.grey[100],
        }}
      >
        <Toolbar /> {/* Spacer for AppBar */}

        <Container maxWidth={false} sx={{ py: 3 }}>
          {shouldBlockOutlet ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading sites...</Typography>
            </Box>
          ) : shouldShowSitesError ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
              <Alert severity="error">Failed to load sites. Please refresh the page.</Alert>
            </Box>
          ) : (
            <Outlet />
          )}
        </Container>
      </Box>

      {/* Change Password Modal */}
      <ChangePasswordModal
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
    </Box>
  );
};

export default AppLayout;
